/* ============================================================
   SAM-198 — Units section headcount warning.

   Rodrigo's proposal (confluence-12.4/12.4-page.txt, adopted by Abel):
   "The admin warns at save time when the guest limits add up to more than
   a unit's capacity (e.g. 5 included + 3 extra on a 6-person cabana)."

   Prototype reference (SAM-services-admin-prototype/preview-proposed/
   services-flow.js `unitCapacityWarning()`): sum every tier group's
   `max_total` — that IS "included + extra", since pricing.py's
   `_tier_group_plan` validates `max_total` as the cap on the SUMMED
   requested quantity across a group's `tiers`, included guests counted in
   (functions/booking/pricing.py, sam-lambda-functions@origin/dev) — then
   flag every unit whose own capacity is smaller than that sum. Pricing caps
   are SAM's; physical capacity is the booking engine's. They must agree, or
   a booking that passes pricing here gets rejected by the engine.

   Pure helper, no React — used by UnitsSection.tsx (inline, per unit) and by
   useServiceEditor's save-bar validation (one summarized entry).
   ============================================================ */
import type { ResourceForm } from '../../components/ServiceResourcesEditor';
import type { TierGroupForm } from '../../components/ServiceTierGroupsEditor';

export interface UndersizedUnit {
  name: string;
  capacity: number;
}

export interface HeadcountWarning {
  /** Sum of every tier group's max_total — the guest limit the pricing side promises. */
  neededGuests: number;
  /** Units whose own capacity can't seat that many. */
  undersized: UndersizedUnit[];
}

/**
 * Returns null when there is nothing to warn about: no units at all (nothing to check
 * capacity against — SAM-546's single-implicit-resource behaviour is unchanged), or no tier
 * group sets a max_total, or every unit's capacity already covers the summed guest limit.
 */
export function computeHeadcountWarning(
  resources: ResourceForm[],
  tierGroups: TierGroupForm[],
): HeadcountWarning | null {
  if (resources.length === 0) return null;

  const neededGuests = tierGroups.reduce((sum, g) => sum + (Number(g.max_total) || 0), 0);
  if (neededGuests <= 0) return null;

  const undersized: UndersizedUnit[] = resources
    .filter((r) => (Number(r.capacity) || 0) < neededGuests)
    .map((r) => ({ name: r.name || r.key, capacity: Number(r.capacity) || 0 }));

  if (undersized.length === 0) return null;

  return { neededGuests, undersized };
}

/** One-line summary for the save bar — the per-unit editor shows the full breakdown inline. */
export function formatHeadcountWarning(warning: HeadcountWarning): string {
  const { neededGuests, undersized } = warning;
  const names = undersized.map((u) => u.name).join(', ');
  return `Guest limits add up to ${neededGuests}, but ${undersized.length} of your units have less `
    + `capacity (${names}). A booking would pass pricing and be rejected by the booking engine.`;
}
